import { withMotion } from '@hoc/withMotion';
import useOnClickOutside from '@hooks/useClickOutside';
import Search from '@img/icons/header/search.svg';
import Chevron from '@img/icons/icon-chevron-down.svg';
import Microphone from '@img/icons/microphone.svg';
import Recording from '@img/icons/recording.svg';
import * as React from 'react';
import { SearchCategoryModel } from '../../Header.types';
import S from './SearchInput.styles';

export type SearchInputProps = {
  animatedPlaceholder?: boolean;
  placeholder: string;
  listeningPlaceholder: string;
  ariaCurrent?: boolean;
  searchTerm: string;
  searchCategory: number;
  setSearchCategory: (searchCategory: number) => void;
  setSearchTerm: (searchTerm: string) => void;
  onFocus: () => void;
  onSearch: (searchTerm: string) => void;
  onSubmit: (searchTerm: string) => void;
  searchCategories?: SearchCategoryModel[];
  dropdownOpen: boolean;
  device: 'mobile' | 'desktop' | 'unknown';
  inputFocused?: boolean;
  setDropdownOpen: (dropdownOpen: boolean) => void;
};

const SearchInput: React.FC<SearchInputProps> = ({
  animatedPlaceholder = false,
  placeholder,
  listeningPlaceholder,
  ariaCurrent,
  searchTerm,
  setSearchTerm,
  searchCategory,
  setSearchCategory,
  onFocus,
  onSearch,
  onSubmit,
  searchCategories = [],
  dropdownOpen,
  device,
  inputFocused = false,
  setDropdownOpen,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const placeholderRef = React.useRef<HTMLSpanElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [_searchTerm, _setSearchTerm] = React.useState(searchTerm);
  const [dropdownWidth, setDropdownWidth] = React.useState(0);
  const [recognition, setRecognition] = React.useState<SpeechRecognition | null>(null);
  const [isListening, setIsListening] = React.useState(false);
  const [inputWidth, setInputWidth] = React.useState(0);
  const [isFocused, setIsFocused] = React.useState(false);
  const [placeholderWidth, setPlaceholderWidth] = React.useState(0);
  const [permissionStatus, setPermissionStatus] = React.useState<PermissionState | null>(null);
  useOnClickOutside(dropdownRef, () => setDropdownOpen(false));

  const micPadding = permissionStatus !== 'denied' ? 36 : 0;
  const mobileSearchPadding = device === 'mobile' ? micPadding + 50 : micPadding + 14;
  const scrollDuration = device === 'mobile' ? getScrollDuration() * 3 : getScrollDuration() / 1.5;

  React.useEffect(() => {
    setSearchTerm(_searchTerm.trim());
  }, [_searchTerm]);

  React.useEffect(() => {
    if (inputFocused) {
      if (isFocused) {
        return;
      }
      if (inputRef.current) {
        inputRef.current.focus();
        setIsFocused(true);
      }
    }
  }, [inputFocused]);

  React.useEffect(() => {
    const checkIfFocused = (): void => {
      if (!inputRef.current) {
        return;
      }
      // Is element is focused before hydration
      if (document.activeElement === inputRef.current) {
        setIsFocused(true);
        onFocus();

        const value = inputRef.current.value;

        if (value) {
          _setSearchTerm(value);
          onSearch(value);
        }
      }
    };

    const initRecognition = (): void => {
      // Check if the browser supports the SpeechRecognition API
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.error('Your browser does not support the Speech Recognition API. Please try using Chrome or Firefox.');
        return;
      }

      // Check microphone permissions
      navigator.permissions.query({ name: 'microphone' as PermissionName }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => {
          setPermissionStatus(result.state);
        };
      });

      // Create a new instance of SpeechRecognition
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.lang = 'en-US';

      // Define the onresult event handler
      recognitionInstance.onresult = (event) => {
        // Get the most recent result
        const result = event.results[event.resultIndex];
        if (result.isFinal) {
          // Log the recognized text to the console
          let recognisedText = result[0].transcript;

          if (recognisedText.endsWith('.')) {
            recognisedText = recognisedText.slice(0, -1);
          }

          _setSearchTerm(recognisedText);
          onSearch(recognisedText);
          recognitionInstance.stop();
          setIsListening(false);
        }
      };

      // Define the onerror event handler
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          setIsListening(false);
          // setError('Microphone access was blocked. Please allow microphone access in your browser settings.');
        } else {
          setIsListening(false);
          // setError(`Speech recognition error: ${event.error}`);
        }

        recognitionInstance.stop();
      };

      // Set the recognition instance to state
      setRecognition(recognitionInstance);
    };

    checkIfFocused();
    initRecognition();
  }, []);

  // Measure the width of the placeholder text and the input box
  React.useEffect(() => {
    if (dropdownRef.current) {
      setDropdownWidth(dropdownRef.current.offsetWidth); // Set the width of the dropdown
    }
    if (placeholderRef.current) {
      setPlaceholderWidth(placeholderRef.current.offsetWidth); // Set the width of the placeholder
    }
    if (inputRef.current) {
      setInputWidth(inputRef.current.offsetWidth); // Set the width of the input
    }
  }, [placeholder, device, searchCategory]);

  React.useEffect(() => {
    if (ariaCurrent || inputRef.current === null) {
      return;
    }

    inputRef.current.value = '';
  }, [ariaCurrent]);

  return (
    <S.Container aria-current={ariaCurrent} data-animated-placeholder={animatedPlaceholder}>
      {searchCategories.length > 0 ? (
        <S.Dropdown
          ref={dropdownRef}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          data-is-open={dropdownOpen}
          data-max-width={dropdownWidth > 200}
        >
          <S.DropdownSelected>{searchCategories[searchCategory].displayName}</S.DropdownSelected>
          <Chevron />
          {dropdownOpen ? (
            <S.DropdownItems>
              {searchCategories.map((x, i) => (
                <S.DropdownItem
                  key={x.displayName}
                  onClick={(e) => {
                    e.stopPropagation();

                    setSearchCategory(i);
                    setDropdownOpen(false);
                  }}
                  aria-selected={i === searchCategory}
                >
                  {x.displayName}
                </S.DropdownItem>
              ))}
            </S.DropdownItems>
          ) : null}
        </S.Dropdown>
      ) : null}
      <S.InputWrapper>
        {animatedPlaceholder && (
          <>
            <span
              ref={placeholderRef}
              style={{
                position: 'absolute',
                whiteSpace: 'nowrap',
                visibility: 'hidden', // Keep it hidden, only used for measuring
                fontSize: '14px',
                paddingLeft: '18px',
              }}
            >
              {isListening ? listeningPlaceholder : placeholder}
            </span>
            {!isFocused && _searchTerm === '' && (
              <S.InputPlaceholder
                key={`${
                  searchCategories.length > 0 ? searchCategories[searchCategory].displayName : 'placeholder'
                } - ${placeholder}`}
                initial={{ x: 0, opacity: 1 }} // Start at the left with full opacity
                animate={
                  placeholderWidth > inputWidth
                    ? { x: -(placeholderWidth - inputWidth + mobileSearchPadding), opacity: 1 }
                    : { opacity: 1 }
                } // Scroll the text, keep it visible
                exit={{ opacity: 0 }} // Fade out when focus is gained
                transition={{
                  repeat: Infinity,
                  duration: scrollDuration,
                  ease: 'linear',
                  delay: 0.5,
                  repeatDelay: 1,
                }}
              >
                {isListening ? listeningPlaceholder : placeholder}
              </S.InputPlaceholder>
            )}
          </>
        )}
        <S.Input
          ref={inputRef}
          type="text"
          placeholder={isListening ? listeningPlaceholder : placeholder}
          aria-current={ariaCurrent}
          data-has-mic={permissionStatus !== 'denied'}
          value={_searchTerm}
          onFocus={() => {
            setIsFocused(true); // Hide placeholder when focused
            onFocus();
          }}
          onBlur={() => setIsFocused(false)} // Show placeholder when unfocused
          onChange={(e) => {
            _setSearchTerm(e.target.value);
            onSearch(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();

              onSubmit(_searchTerm);
            }
          }}
        />
      </S.InputWrapper>

      {permissionStatus !== 'denied' ? (
        <S.MicIcon
          aria-label="Voice search"
          onClick={() => {
            if (!isListening) {
              recognition?.start();
              setIsListening(true);
            } else {
              recognition?.stop();
              setIsListening(false);
            }
          }}
        >
          {isListening ? <Recording /> : <Microphone />}
        </S.MicIcon>
      ) : null}
      <S.Button aria-label="Search" onClick={() => onSubmit(_searchTerm)}>
        <Search />
      </S.Button>
    </S.Container>
  );

  function getScrollDuration(): number {
    switch (true) {
      case inputWidth < 100:
        return 2;
      case inputWidth < 200:
        return 3;
      case inputWidth < 300:
        return 4;
      case inputWidth < 400:
        return 5;
      default:
        return 5;
    }
  }
};

export default withMotion(SearchInput);
